import React, { useEffect, useState } from 'react';
import {
  Button,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  AlertDialogCloseButton,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import isEmpty from 'lodash/isEmpty';
import InputController from 'components/Form/InputController';
import { FileImageValid, ModalType, ROOT_API } from 'constants/common';
import { toast } from 'components/Toast';
import { FaceLinkFormValidate } from 'utils/validation';
import FileSelectController from 'components/Form/FileSelectController';
import { ErrorForm } from 'constants/error';
import { useCreateFacebookLinkMutation, useUpdateFacebookLinkMutation, uploadFacebookImage } from 'services/link';

const CreateLinkModal = ({ isOpen, linkDetail, onClose, refetch }) => {
  const cancelRef = React.useRef();

  const [fileSelected, setFileSelected] = useState({
    image: null,
  });
  const [error, setError] = useState({
    image: null,
  });

  const createScriptMutation = useCreateFacebookLinkMutation();
  const updateScriptMutation = useUpdateFacebookLinkMutation();

  const { control, handleSubmit, reset } = useForm({
    resolver: yupResolver(FaceLinkFormValidate),
    defaultValues: {
      linkRedirect: '',
    },
  });

  useEffect(() => {
    if (!isEmpty(linkDetail)) {
      reset({
        linkRedirect: linkDetail.linkRedirect,
      });
      linkDetail?.linkImage && setFileSelected({ image: { filePreview: linkDetail?.linkImage } });
    }
  }, [linkDetail]);

  const handleSuccess = () => {
    toast.showMessageSuccess(`${linkDetail ? 'Cập nhật' : 'Tạo'} đường dẫn thành công`);
    refetch?.();
    onClose(ModalType.Add);
  };

  const handleError = error => {
    toast.showMessageError(
      error?.response?.data?.errors?.[0]?.msg || error?.response?.data?.msg || `${linkDetail ? 'Cập nhật' : 'Tạo'} đường dẫn thành công`
    );
  };

  const onUploadPhoto = async () => {
    const formData = new FormData();

    if (!!fileSelected?.image?.file) {
      formData.append('imageFile', fileSelected?.image?.file);

      const response = await uploadFacebookImage(formData);

      return response?.data;
    }

    return null;
  };

  const onSubmit = async values => {
    if (!fileSelected?.image?.filePreview) {
      handleErrorFile('image', ErrorForm.Required);
      return;
    }

    const photosPath = await onUploadPhoto();

    const dataSubmit = {
      ...values,
      ...(photosPath && { linkImage: photosPath }),
    };

    if (!isEmpty(linkDetail)) {
      updateScriptMutation.mutate(
        { ...dataSubmit, id: linkDetail?._id },
        {
          onSuccess: () => {
            handleSuccess();
          },
          onError: error => handleError(error),
        }
      );

      return;
    }

    createScriptMutation.mutate(
      { ...dataSubmit },
      {
        onSuccess: () => handleSuccess(),
        onError: error => handleError(error),
      }
    );
  };

  const handleFileSelect = (fieldName, e) => {
    if (e?.target?.files?.[0]) {
      const fileSelected = e.target.files[0];
      const filePreview = URL.createObjectURL(fileSelected);
      const extensionFile = fileSelected?.name?.split('.')?.pop()?.toLowerCase();

      if ([...FileImageValid].includes(extensionFile)) {
        setFileSelected(prev => ({
          ...prev,
          [fieldName]: { file: fileSelected, filePreview },
        }));

        return;
      }

      setFileSelected(prev => ({
        ...prev,
        [fieldName]: null,
      }));
      handleErrorFile(fieldName, 'File chỉ hỗ trợ định dạng .png, .jpeg, .jpg');
    }
  };

  const handleErrorFile = (fieldName, value) => {
    setError(prev => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  return (
    <>
      <AlertDialog
        motionPreset="slideInBottom"
        leastDestructiveRef={cancelRef}
        onClose={() => {
          onClose(ModalType.Add);
        }}
        isOpen={isOpen}
        isCentered
      >
        <AlertDialogOverlay />
        <AlertDialogContent maxW={'600px'}>
          <AlertDialogHeader textTransform="uppercase">{linkDetail ? 'Cập nhật' : 'Tạo'} Script</AlertDialogHeader>
          <AlertDialogCloseButton />
          <AlertDialogBody>
            <form>
              <FileSelectController
                isRequired
                label="Ảnh"
                name="image"
                imgHeight="300px"
                imgWidth="300px"
                filePreview={fileSelected.image?.filePreview}
                error={error.image}
                handleFileSelect={handleFileSelect}
                handleErrorFile={handleErrorFile}
              />
              <InputController control={control} name="linkRedirect" label="Đường dẫn" styleContainer={{ mt: 3 }} isRequired />
            </form>
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button
              ref={cancelRef}
              onClick={() => {
                onClose(ModalType.Add);
              }}
            >
              Hủy
            </Button>
            <Button colorScheme="blue" ml={3} isLoading={createScriptMutation.isPending} onClick={handleSubmit(onSubmit)}>
              {linkDetail ? 'Cập nhật' : 'Tạo'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default CreateLinkModal;
